/**
 * DISPONIBILIDAD — el histórico, con su letra pequeña delante.  (T047, FR-030d)
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  EL AVISO DE "CUÁNTO OBSERVAMOS" VA **ARRIBA**, NO EN UN PIE DE PÁGINA.   ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * Un "99,8%" es una cifra que la gente lee, se cree y repite en una reunión. Si
 * está calculada sobre el 4% del periodo, ese dato es basura con aspecto de
 * rigor — y el que lo repita quedará retratado por nuestra culpa, no por la suya.
 *
 * Por eso la cobertura se enseña ANTES que los porcentajes y con el mismo peso
 * visual. Cuando es baja, se dice con todas las letras que los números de abajo
 * no significan gran cosa todavía. Enterrarlo en un asterisco sería tener la
 * información y esconderla, que es peor que no tenerla.
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { api } from '../lib/api'

interface Disp {
    pct: number | null
    segundosObservados: number
    segundosPeriodo: number
    segundosArriba: number
    segundosDegradado: number
    segundosCaido: number
    segundosSinEstado: number
}

interface Historico {
    periodo: { desde: string; hasta: string }
    observado: { segundos: number; segundosPeriodo: number; pct: number }
    componentes: { component: string; disponibilidad: Disp }[]
    incidencias: { component: string; state: string; at: string; detail: string | null }[]
}

const DIAS = [
    { clave: 7, etiqueta: '7 días' },
    { clave: 30, etiqueta: '30 días' },
] as const

/** Cobertura por debajo de la cual los porcentajes no merecen confianza. */
const COBERTURA_FLOJA = 80

function duracion(seg: number): string {
    if (seg < 60) return `${seg} s`
    if (seg < 3600) return `${Math.round(seg / 60)} min`
    if (seg < 86_400) return `${Math.round(seg / 3600)} h`
    return `${Math.round(seg / 86_400)} días`
}

/**
 * `agenda:2` no le dice nada a nadie. El nombre del negocio se pasa desde fuera
 * (la vista ya lo tiene de la salud en vivo): no se inventa aquí ni se pide dos
 * veces.
 */
function nombrar(component: string, agendas: Map<string, string>): string {
    if (component === 'servidor') return 'Servidor'
    if (component === 'bot') return 'Bot (Agendify)'
    if (component === 'chatwoot') return 'Chatwoot'
    return agendas.get(component) ?? component.replace(/^agenda:/, 'Agenda ')
}

const TONO_ESTADO: Record<string, string> = {
    down: 'text-rose-400',
    degraded: 'text-amber-400',
    unconfigured: 'text-neutral-500',
    up: 'text-emerald-400',
}

export function Disponibilidad({ agendas }: { agendas: Map<string, string> }) {
    const [dias, setDias] = useState<number>(7)

    const { data, isPending, isError } = useQuery<Historico>({
        queryKey: ['salud-historico', dias],
        queryFn: () => api.get<Historico>(`/overview/health/history?dias=${String(dias)}`),
    })

    if (isPending) return <p className="text-sm text-neutral-500">Cargando el histórico…</p>
    if (isError || !data) return <p className="text-sm text-rose-400">No se pudo cargar el histórico.</p>

    const cobertura = data.observado.pct
    const floja = cobertura < COBERTURA_FLOJA

    return (
        <section className="space-y-3 rounded-lg border border-neutral-800 p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Disponibilidad</h2>
                <div className="flex rounded-md border border-neutral-800">
                    {DIAS.map((d) => (
                        <button
                            key={d.clave}
                            onClick={() => setDias(d.clave)}
                            className={
                                'min-h-11 px-3 py-1 text-xs transition md:min-h-0 ' +
                                (dias === d.clave
                                    ? 'bg-neutral-800 text-neutral-100'
                                    : 'text-neutral-500 hover:text-neutral-300')
                            }
                        >
                            {d.etiqueta}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── LA LETRA PEQUEÑA, PRIMERO Y EN GRANDE ──────────────────────── */}
            <div
                className={
                    'rounded-md px-3 py-2 text-xs ' +
                    (floja ? 'bg-amber-950/40 text-amber-300' : 'bg-neutral-900/60 text-neutral-400')
                }
            >
                {floja ? '⚠️ ' : ''}
                Observamos <strong>{duracion(data.observado.segundos)}</strong> de los{' '}
                {duracion(data.observado.segundosPeriodo)} del periodo — <strong>{cobertura}%</strong> de cobertura.
                {floja && ' Los porcentajes de abajo se calculan SOLO sobre ese tramo: aún dicen poco.'}
            </div>

            <div className="space-y-2">
                {data.componentes.map((c) => {
                    const d = c.disponibilidad
                    const tono = d.pct === null ? 'bg-neutral-700' : d.pct >= 99 ? 'bg-emerald-500' : d.pct >= 95 ? 'bg-amber-500' : 'bg-rose-500'
                    return (
                        <div key={c.component}>
                            <div className="flex items-baseline justify-between gap-2 text-xs">
                                <span className="text-neutral-300">{nombrar(c.component, agendas)}</span>
                                <span className="tabular-nums text-neutral-400">
                                    {/* `null` = no observamos nada. Ni 0 ni 100: un guion. */}
                                    {d.pct === null ? '— sin datos' : `${d.pct}%`}
                                </span>
                            </div>
                            <div className="mt-1 h-1 overflow-hidden rounded-full bg-neutral-800">
                                <div className={`h-full ${tono}`} style={{ width: `${d.pct ?? 0}%` }} />
                            </div>
                            {(d.segundosCaido > 0 || d.segundosDegradado > 0) && (
                                <p className="mt-1 text-[10px] text-neutral-500">
                                    {d.segundosCaido > 0 && `caído ${duracion(d.segundosCaido)}`}
                                    {d.segundosCaido > 0 && d.segundosDegradado > 0 && ' · '}
                                    {d.segundosDegradado > 0 && `degradado ${duracion(d.segundosDegradado)}`}
                                </p>
                            )}
                        </div>
                    )
                })}
            </div>

            {/* ── LAS INCIDENCIAS ─────────────────────────────────────────────
                Aquí SÍ salen los contenedores: no tienen ficha de uptime (un
                contenedor parado a propósito daría un 0% cierto e inútil), pero
                que uno se caiga es exactamente lo que hay que ver. */}
            <div className="border-t border-neutral-900 pt-3">
                <h3 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-neutral-600">
                    Incidencias del periodo
                </h3>
                {data.incidencias.length === 0 ? (
                    <p className="text-xs text-neutral-600">
                        Ninguna. Nada pasó a caído ni a degradado mientras observábamos.
                    </p>
                ) : (
                    <ul className="space-y-1">
                        {data.incidencias.map((i, n) => (
                            <li key={`${i.at}-${i.component}-${String(n)}`} className="flex items-baseline gap-2 text-xs">
                                <span className="tabular-nums text-neutral-600">
                                    {new Date(i.at).toLocaleString('es-ES', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}
                                </span>
                                <span className="text-neutral-300">
                                    {nombrar(i.component, agendas).replace(/^container:/, '')}
                                </span>
                                <span className={TONO_ESTADO[i.state] ?? 'text-neutral-400'}>{i.state}</span>
                                {i.detail && <span className="ml-auto truncate pl-2 text-neutral-600">{i.detail}</span>}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    )
}
